function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/* eslint-disable max-statements */
import React from 'react';
import { AppError } from 'src/redux/types';
import styles from './styles.module.scss';
import icClose from 'assets/svg/ic_close.svg';
import icThetanCoin from 'assets/img/thetan_coin.png';
import icQuestionMark from 'assets/svg/ic_help_v2.svg';
import Modal from 'src/Components/Modal';
import QuantityBattle from '../Components/QuantityBattle';
import Checkbox from 'src/Components/CheckboxV2';
import { useTypedDispatch, useTypedSelector } from 'src/redux/utils';
import { calculatePredictedReward, calculateRentPeriodSecond, DEFAULT_WIN_RATE, formatErrorMessage, getPriceAfterFee, getRentPeriodMessage, MAX_PRICE, validateInputPrice } from './utils';
import { fetchHeroDetail, prepareRentOutItem, rentOutItem } from 'src/redux/actions';
import { CurrencyType, normalNumberToSystemCurrency } from '@wolffun/system-currency';
import ErrorMessage from './ErrorMessage';
import { ReactTooltip, TooltipPosition } from 'src/Components/TooltipProviderV2';
import Button from '../../Components/Button';
import { useConfirmOpenWalletConnect } from 'src/Components/WalletConnectConfirmProvider';
import { useTestId } from '@wolffun/testable-id';

const RentOutHeroModal = ({
  hero,
  onClose,
  onCompleted
}) => {
  const {
    generateTestIdProps
  } = useTestId();
  const dispatch = useTypedDispatch();
  const {
    confirmOpenWalletConnect
  } = useConfirmOpenWalletConnect();
  const rentOutLoading = useTypedSelector(state => state.rentOutItem.loading);
  const rentalConfig = useTypedSelector(state => state.itemRentalConfigs.data);
  const battleReward = useTypedSelector(state => state.heroDescriptionConfigs.reward);
  const feeInPercent = (rentalConfig === null || rentalConfig === void 0 ? void 0 : rentalConfig.feeRentOutPer) || 0;
  const remainRentBattles = hero.hero.totalBattleCapTHC - hero.hero.battleCapTHC;
  const [isUserAccept, setIsUserAccept] = React.useState(false);
  const [price, setPrice] = React.useState(0);
  const [battle, setBattle] = React.useState((rentalConfig === null || rentalConfig === void 0 ? void 0 : rentalConfig.minTHCBattles) || 0);
  const [error, setError] = React.useState(AppError.NO_ERROR);
  const rentPeriod = React.useMemo(() => {
    if (!rentalConfig) return '';
    return getRentPeriodMessage({
      rentBattle: battle,
      hero,
      rentalConfig
    });
  }, [battle, hero, rentalConfig]);

  const rentOutHero = async () => {
    const systemPrice = normalNumberToSystemCurrency(price, CurrencyType.THC);

    if (!rentalConfig) {
      return;
    }

    const preparedData = await dispatch(prepareRentOutItem({
      numberBattle: battle,
      rentPrice: systemPrice,
      tokenId: hero.hero.tokenId,
      lockDuration: calculateRentPeriodSecond({
        hero,
        rentBattle: battle,
        rentalConfig
      })
    }));
    confirmOpenWalletConnect(async () => {
      const {
        error: rentOutError
      } = await dispatch(rentOutItem(preparedData));

      if (rentOutError) {
        setError(rentOutError);
        dispatch(fetchHeroDetail(hero.hero.id));
        return;
      }

      onCompleted({
        hero,
        price: systemPrice,
        battle,
        feeInPercent,
        priceAfterFee: normalNumberToSystemCurrency(getPriceAfterFee(price, feeInPercent), CurrencyType.THC)
      });
    });
  };

  React.useEffect(() => {
    const validateError = validateInputPrice({
      price,
      battle,
      maxRentBattles: remainRentBattles,
      rentalConfig
    });
    setError(validateError);
  }, [price, battle, remainRentBattles, rentalConfig]);

  const renderTooltip = () => {
    if (!battleReward) return '';
    return /*#__PURE__*/React.createElement("div", {
      className: styles.tooltipContainer
    }, /*#__PURE__*/React.createElement("span", {
      className: styles.tooltip
    }, "Predicted THC earned by Renter with win rate of\xA0", DEFAULT_WIN_RATE * 100, "%:", /*#__PURE__*/React.createElement("span", {
      className: styles.currencyValue
    }, "\xA0", calculatePredictedReward({
      baseBonus: hero.hero.thcBonus,
      lostBonus: battleReward.lose,
      winBonus: battleReward.win,
      rentBattle: battle,
      winRate: DEFAULT_WIN_RATE
    }), "\xA0THC")));
  };

  return /*#__PURE__*/React.createElement(Modal, null, /*#__PURE__*/React.createElement("div", {
    className: styles.modal
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.header
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "Rent out hero"), /*#__PURE__*/React.createElement("button", {
    className: styles.close,
    disabled: rentOutLoading,
    onClick: onClose
  }, /*#__PURE__*/React.createElement("img", {
    src: icClose
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.detailContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.subtitle
  }, "Put up hero #", hero.hero.tokenId, " - ", hero.skin.name, "\xA0", hero.heroType.name, " on the Marketplace for rent"), /*#__PURE__*/React.createElement("span", {
    className: styles.quantityTitle
  }, "Battles for rent"), /*#__PURE__*/React.createElement(QuantityBattle, {
    minBattle: rentalConfig === null || rentalConfig === void 0 ? void 0 : rentalConfig.minTHCBattles,
    maxBattle: remainRentBattles,
    onChanged: setBattle
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.priceRow
  }, /*#__PURE__*/React.createElement("input", _extends({
    className: styles.price,
    value: String(price),
    type: "number",
    maxLength: 6,
    onChange: e => {
      const value = Number(e.target.value) || 0;

      if (value <= MAX_PRICE) {
        setPrice(Math.abs(value));
      }
    }
  }, generateTestIdProps('input-rent-price'))), /*#__PURE__*/React.createElement("div", {
    className: styles.unit
  }, /*#__PURE__*/React.createElement("img", {
    className: styles.currencyIcon,
    src: icThetanCoin
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.currencyName
  }, "THC"), /*#__PURE__*/React.createElement(ReactTooltip, {
    className: styles.tooltip,
    position: TooltipPosition.TOP_LEFT,
    content: renderTooltip()
  }, /*#__PURE__*/React.createElement("img", {
    className: styles.questionIcon,
    src: icQuestionMark
  })))), /*#__PURE__*/React.createElement("span", _extends({
    className: styles.warning
  }, generateTestIdProps('owner-receive')), "You will receive ", getPriceAfterFee(price, feeInPercent).toFixed(2), ' ', "THC after subtracting a ", feeInPercent, "% fee"), /*#__PURE__*/React.createElement("div", {
    className: styles.confirmSection
  }, /*#__PURE__*/React.createElement(Checkbox, _extends({
    check: isUserAccept,
    onToggle: value => setIsUserAccept(value),
    className: styles.checkbox
  }, generateTestIdProps('understand'))), /*#__PURE__*/React.createElement("span", {
    className: styles.content
  }, "I understand that all THC and other rewards earned from rented battles goes to Renter")), error !== AppError.NO_ERROR ? /*#__PURE__*/React.createElement(ErrorMessage, {
    className: styles.error,
    title: "Opps! Something went wrong",
    description: formatErrorMessage(error, {
      minPrice: rentalConfig === null || rentalConfig === void 0 ? void 0 : rentalConfig.minTHCPrice.toString(),
      maxBattle: remainRentBattles.toString(),
      minBattle: rentalConfig === null || rentalConfig === void 0 ? void 0 : rentalConfig.minTHCBattles.toString(),
      maxPrice: rentalConfig === null || rentalConfig === void 0 ? void 0 : rentalConfig.maxTHCPrice.toString()
    })
  }) : undefined, rentPeriod ? /*#__PURE__*/React.createElement("div", {
    className: styles.wrapFooter
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.periodLabel
  }, "Renter will keep the hero for maximum"), /*#__PURE__*/React.createElement("span", _extends({
    className: styles.periodValue
  }, generateTestIdProps('rental-period')), rentPeriod)) : undefined, /*#__PURE__*/React.createElement("div", {
    className: styles.buttonGroup
  }, rentOutLoading ? /*#__PURE__*/React.createElement(Button, _extends({
    disabled: true,
    loading: rentOutLoading,
    title: "Processing..."
  }, generateTestIdProps('primary-button'))) : /*#__PURE__*/React.createElement(Button, _extends({
    title: "Put up for rent",
    onClick: rentOutHero,
    disabled: !isUserAccept || error !== AppError.NO_ERROR
  }, generateTestIdProps('primary-button')))))));
};

export default RentOutHeroModal;